import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "@phosphor-icons/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faqs`);
        setFaqs(response.data);
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  const filteredFaqs = activeTab === "all" 
    ? faqs 
    : faqs.filter(faq => faq.category === activeTab);

  const tabs = [
    { id: "all", label: "All Questions" },
    { id: "sellers", label: "For Sellers" },
    { id: "buyers", label: "For Buyers" }
  ];

  return (
    <div data-testid="faq-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
            Estate Sale FAQ - Fargo ND & Moorhead MN
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-[#1C1914] leading-tight mb-6">
            Estate Sale Questions & Answers
          </h1>
          <p className="text-xl text-[#5C564D] leading-relaxed max-w-2xl mx-auto">
            Find answers to common questions about estate sales in Fargo, ND, Moorhead, MN, and the surrounding Fargo-Moorhead area. Whether you're planning an estate sale, need help with downsizing, or looking for estate sales today or this weekend, we've got you covered.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeTab === tab.id
                    ? "bg-[#2C4C3B] text-white"
                    : "bg-[#F7F5F0] text-[#5C564D] hover:bg-[#E2DCD0]"
                }`}
                data-testid={`faq-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#5C564D] text-lg">Loading questions...</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-[#F7F5F0] border border-[#E2DCD0] rounded-lg px-6 md:px-8"
                >
                  <AccordionTrigger
                    className="text-left text-[#1C1914] font-medium py-6 hover:no-underline text-lg md:text-xl"
                    data-testid={`faq-question-${index}`}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#5C564D] pb-6 leading-relaxed text-base md:text-lg">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-[#E2DCD0] p-8 md:p-12 rounded-lg text-center">
            <h2 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914] mb-4">
              Still Have Questions?
            </h2>
            <p className="text-[#5C564D] mb-8 max-w-lg mx-auto">
              We're happy to answer any questions you have about our estate sale services. Give us a call or send us a message.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:2182982420"
                className="inline-flex items-center justify-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
                data-testid="faq-phone-button"
              >
                <Phone weight="fill" className="w-5 h-5" />
                Call 218-298-2420
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
                data-testid="faq-contact-button"
              >
                Contact Us
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-serif font-medium text-[#1C1914] mb-8 text-center">
            Learn More About Estate Sales
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/about"
              className="bg-[#F7F5F0] p-6 rounded-lg hover:shadow-md transition-all duration-300 group"
              data-testid="faq-link-about"
            >
              <h3 className="text-lg font-serif font-medium text-[#1C1914] mb-2 group-hover:text-[#2C4C3B] transition-colors">About Us</h3>
              <p className="text-[#5C564D] text-sm">Learn about our team and approach to estate sales.</p>
            </Link>
            <Link
              to="/service-areas"
              className="bg-[#F7F5F0] p-6 rounded-lg hover:shadow-md transition-all duration-300 group"
              data-testid="faq-link-areas"
            >
              <h3 className="text-lg font-serif font-medium text-[#1C1914] mb-2 group-hover:text-[#2C4C3B] transition-colors">Service Areas</h3>
              <p className="text-[#5C564D] text-sm">See all the communities we serve in ND and MN.</p>
            </Link>
            <Link
              to="/blog"
              className="bg-[#F7F5F0] p-6 rounded-lg hover:shadow-md transition-all duration-300 group"
              data-testid="faq-link-blog"
            >
              <h3 className="text-lg font-serif font-medium text-[#1C1914] mb-2 group-hover:text-[#2C4C3B] transition-colors">Our Blog</h3>
              <p className="text-[#5C564D] text-sm">Read helpful articles about estate sales.</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
